import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import { Search, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

export const PayrollVisibility = ({ selectedEntity }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const res = await fetch(`${API_URL}/api/payroll/visibility${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setRows(await res.json());
    } catch { toast.error('Failed to load visibility settings'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const setVisibility = async (employee_ids, visible) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/visibility`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ employee_ids, visible })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to update');
      toast.success(d.message);
      setRows(prev => prev.map(r => employee_ids.includes(r.employee_id) ? { ...r, payroll_visible: visible } : r));
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const filtered = rows.filter(r => {
    const s = search.toLowerCase();
    return !s || (r.employee_name || '').toLowerCase().includes(s) || (r.employee_id || '').toLowerCase().includes(s);
  });
  const visibleCount = rows.filter(r => r.payroll_visible).length;

  return (
    <div className="space-y-4" data-testid="payroll-visibility">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        Salary appears in an employee's login <strong>only when their switch is ON</strong>. Turn it on to release, off to hide.
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search employee..." className="pl-10" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="pv-search" />
        </div>
        <Badge variant="outline" className="border-emerald-300 text-emerald-700">{visibleCount} visible</Badge>
        <Badge variant="outline" className="border-slate-300 text-slate-500">{rows.length - visibleCount} hidden</Badge>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving || rows.length === 0}
          onClick={() => setVisibility(filtered.map(r => r.employee_id), true)} data-testid="pv-show-all-btn">
          <Eye className="w-4 h-4 mr-1.5" />Show All
        </Button>
        <Button size="sm" variant="outline" className="border-red-400 text-red-600 hover:bg-red-50" disabled={saving || rows.length === 0}
          onClick={() => setVisibility(filtered.map(r => r.employee_id), false)} data-testid="pv-hide-all-btn">
          <EyeOff className="w-4 h-4 mr-1.5" />Hide All
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead className="text-center">Salary Visible in Login</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.employee_id} data-testid={`pv-row-${r.employee_id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}</TableCell>
                      <TableCell className="text-xs">{r.designation || '—'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch checked={r.payroll_visible} disabled={saving}
                            onCheckedChange={(v) => setVisibility([r.employee_id], v)}
                            data-testid={`pv-toggle-${r.employee_id}`} />
                          {saving ? <Loader2 className="w-3 h-3 animate-spin text-slate-300" /> :
                            <span className={`text-[10px] font-semibold ${r.payroll_visible ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {r.payroll_visible ? 'VISIBLE' : 'HIDDEN'}
                            </span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500 text-sm">No employees found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
